import { useState, useEffect } from "react";
import i18n from "../i18n/i18n";


interface IntroSectionProps {
  name: string;
  onComplete: () => void;
}

const IntroSection = ({ name, onComplete }: IntroSectionProps) => {
  const [showFirstText, setShowFirstText] = useState(true);
  const [showSecondText, setShowSecondText] = useState(false);
  const [displayedSubtext, setDisplayedSubtext] = useState("");
  const [displayedSecondText, setDisplayedSecondText] = useState("");
  const [showSubtext, setShowSubtext] = useState(false);

  const subtextMessage = i18n.t('i_like_your_name');
  const secondMessage = i18n.t('i_like_everything');

  useEffect(() => {
    // First text (Hi name) appears immediately
    // Subtext appears after 2 seconds delay
    const timer1 = setTimeout(() => {
      setShowSubtext(true);
    }, 2000);

    // Both fade out after 5 seconds total
    const timer2 = setTimeout(() => {
      setShowFirstText(false);
    }, 5000);

    // Second text appears after 1.5 second gap
    const timer3 = setTimeout(() => {
      setShowSecondText(true);
    }, 6500);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [onComplete]);

  // Typing animation for first text's subtext
  useEffect(() => {
    if (showSubtext && displayedSubtext.length < subtextMessage.length) {
      const timer = setTimeout(() => {
        setDisplayedSubtext(subtextMessage.slice(0, displayedSubtext.length + 1));
      }, 60);
      return () => clearTimeout(timer);
    }
  }, [showSubtext, displayedSubtext, subtextMessage]);

  // Typing animation for second text
  useEffect(() => {
    if (showSecondText && displayedSecondText.length < secondMessage.length) {
      const timer = setTimeout(() => {
        setDisplayedSecondText(secondMessage.slice(0, displayedSecondText.length + 1));
      }, 60);
      return () => clearTimeout(timer);
    } else if (displayedSecondText.length === secondMessage.length && showSecondText) {
      // After typing completes, wait 3 seconds then complete
      const timer = setTimeout(() => {
        setShowSecondText(false);
        setTimeout(() => {
          onComplete();
        }, 1000);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showSecondText, displayedSecondText, onComplete, secondMessage]);

  return (
    <section className="min-h-screen flex items-center justify-center px-6">
      <div className="text-center max-w-4xl">
        {showFirstText && (
          <div className="animate-fade-in duration-1000">
            <h1 className="text-6xl md:text-8xl font-bold mb-6 bg-gradient-romantic bg-clip-text text-transparent">
              {i18n.t('hi')} {name}
            </h1>
            {showSubtext && (
              <p className="text-2xl md:text-3xl text-muted-foreground animate-fade-in duration-500">
                {displayedSubtext}
                <span className="animate-pulse">|</span>
              </p>
            )}
          </div>
        )}
        
        {showSecondText && (
          <div className="animate-fade-in duration-1000">
            <p className="text-3xl md:text-5xl font-semibold text-foreground leading-relaxed">
              {displayedSecondText}
              <span className="animate-pulse">|</span>
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default IntroSection;
